// Ad-hoc treasury wallet balance viewer (manager-only).
// GET /api/v1/ops/wallet-balances — Moralis-backed, server-cached 15 min
// (same cadence as the real snapshot job). NOT the Account/PositionSnapshot
// pipeline — see api/apps/portfolio/services/wallet_balances.py.

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { NavSparkline } from '../../components/NavSparkline';
import { PageHeader, SortTable, Stat, TreasuryTabs, SourcesUnavailableBanner } from '../../components/shell';
import { fmtBase } from '../../lib/format';
import { api } from '../../lib/api';
import { usePolling } from '../../lib/usePolling';

const POLL_MS = 60_000;
// The net-worth series is written once nightly — poll it far slower than the
// live balances.
const HISTORY_POLL_MS = 300_000;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// History x values are date-only ISO strings; new Date('2026-08-11') parses as
// UTC midnight and can render the previous day in western timezones, so format
// the string itself.
function fmtDay(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
}

interface BalanceRow {
  wallet_label: string;
  chain: string;
  symbol: string;
  balance: string;
  usd_value: number;
}

interface SkippedEntry {
  wallet_label?: string;
  reason: string;
  // item 29 — see WalletActivity.tsx. Optional so an older cached payload
  // still renders; an outage must be opted IN to the loud side, never
  // defaulted into it.
  kind?: 'error' | 'gap' | 'note';
}

interface AccountEntry {
  label: string;
  total_usd: number | null;
  pct_of_total: number | null;
  manual: boolean;
  updated_by?: string | null;
  updated_at?: string | null;
}

interface FundManualSection {
  fund_id: string;
  fund_name: string;
  subtotal_usd: string | null;
  n_rows: number;
  n_unpriced: number;
}

interface WalletBalancesPayload {
  as_of: string;
  total_usd: number;
  rows: BalanceRow[];
  accounts: AccountEntry[];
  skipped: SkippedEntry[];
  cache_hit: boolean;
  currency: 'USD' | 'EUR';
  fx_eur_per_usd?: number;
  fund_manual_holdings?: FundManualSection[];
}

interface HistoryPayload {
  currency: 'USD' | 'EUR';
  days: number;
  series: { x: string; y: number }[];
  rows: { date: string; partial: boolean }[];
  excluded_partial_days: number;
  excluded_fx_missing_days: number;
}

export default function WalletBalances() {
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [currency, setCurrency] = useState<'USD' | 'EUR'>('EUR');

  const { data, error, loading, refresh } = usePolling<WalletBalancesPayload>(
    (signal) => api<WalletBalancesPayload>(`/ops/wallet-balances?currency=${currency}`, { signal }),
    { intervalMs: POLL_MS, deps: [refreshNonce, currency] },
  );
  // Independent fetch: a failing history endpoint must degrade to no chart,
  // never blank the live balances above it.
  const { data: hist, error: histError } = usePolling<HistoryPayload>(
    (signal) => api<HistoryPayload>(`/ops/wallet-balances/history?currency=${currency}`, { signal }),
    { intervalMs: HISTORY_POLL_MS, deps: [refreshNonce, currency] },
  );

  const handleForceRefresh = () => {
    setRefreshNonce(n => n + 1);
    api(`/ops/wallet-balances?refresh=1&currency=${currency}`).then(() => refresh()).catch(() => refresh());
  };

  if (error) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>
          Failed to load wallet balances.
        </p>
      </div>
    );
  }
  if (!data && loading) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
          Loading…
        </p>
      </div>
    );
  }
  if (!data) return null;

  const asOfText = new Date(data.as_of).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';

  // Only render history that matches the page's current currency — during a
  // toggle refetch (or if the refetch fails) usePolling retains the PREVIOUS
  // currency's payload, and a EUR chart under a USD total is worse than a
  // brief gap.
  const histNow = hist && hist.currency === currency ? hist : null;
  const trendFirst = histNow ? histNow.series[0] : undefined;
  const trendLast = histNow ? histNow.series[histNow.series.length - 1] : undefined;
  const historyNotes: string[] = [];
  if (histNow && histNow.excluded_partial_days > 0) {
    historyNotes.push(
      `${histNow.excluded_partial_days} partial ${histNow.excluded_partial_days === 1 ? 'day' : 'days'} excluded from the net worth trend — a balance source was unavailable.`,
    );
  }
  if (histNow && histNow.currency === 'EUR' && histNow.excluded_fx_missing_days > 0) {
    historyNotes.push(
      `${histNow.excluded_fx_missing_days} ${histNow.excluded_fx_missing_days === 1 ? 'day' : 'days'} without a stored FX rate excluded from the EUR trend.`,
    );
  }

  // The shared SortTable already has a sort arrow, but it's styled
  // invisible until a column is clicked (site-wide default). Rather than
  // touch that shared CSS (used by every table in the app), add a
  // permanently-visible "sortable" hint scoped to just this page's labels.
  const sortableLabel = (text: string) => (
    <>
      {text} <span style={{ opacity: 0.4, fontSize: 10 }}>⇅</span>
    </>
  );

  const columns = [
    { key: 'wallet_label', label: sortableLabel('ACCOUNT') },
    { key: 'chain', label: sortableLabel('CHAIN') },
    { key: 'symbol', label: sortableLabel('TOKEN') },
    {
      key: 'balance', label: sortableLabel('BALANCE'), align: 'right' as const,
      render: (r: BalanceRow) => <span className="num">{Number(r.balance).toLocaleString(undefined, { maximumFractionDigits: 6 })}</span>,
    },
    {
      key: 'usd_value', label: sortableLabel('VALUE'), align: 'right' as const,
      render: (r: BalanceRow) => <span className="num">{fmtBase(r.usd_value, data.currency)}</span>,
    },
  ];

  // Split once, here, so both lists below read from the same rule.
  const errorSkips = data.skipped.filter(s => s.kind === 'error');
  const noteSkips = data.skipped.filter(s => s.kind !== 'error');

  return (
    <div className="view container">
      {/* Above the notice AND the header: it qualifies every figure below. */}
      <SourcesUnavailableBanner down={errorSkips.length} />

      <div style={{
        marginBottom: 20, padding: '10px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'var(--tint)', border: `1px solid var(--accent)`,
        color: 'var(--accent)',
        fontFamily: 'var(--sans)', fontSize: 13,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <path d="M12 3 L22 20 L2 20 Z" stroke="var(--accent)" strokeWidth="1.75" strokeLinejoin="round" />
          <line x1="12" y1="9" x2="12" y2="14" stroke="var(--accent)" strokeWidth="1.75" strokeLinecap="round" />
          <circle cx="12" cy="17" r="1" fill="var(--accent)" />
        </svg>
        This page is still being developed; sources are still being added.
      </div>

      <TreasuryTabs active="balances" />

      <PageHeader
        kicker="OPS"
        title="Treasury wallet balances"
        meta={(
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ display: 'flex', border: 'var(--hairline)' }}>
              {(['USD', 'EUR'] as const).map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  className="label-s"
                  style={{
                    cursor: 'pointer', padding: '4px 12px', border: 'none',
                    background: currency === c ? 'var(--ink)' : 'transparent',
                    color: currency === c ? 'var(--paper)' : 'var(--ink)',
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
            <button type="button" onClick={handleForceRefresh} className="label-s" style={{ cursor: 'pointer' }}>
              REFRESH NOW
            </button>
          </div>
        )}
      />

      <div style={{ display: 'flex', gap: 48, marginBottom: 32 }}>
        <Stat
          label="TOTAL VALUE"
          value={fmtBase(data.total_usd, data.currency)}
          sub={`as of ${asOfText}${data.cache_hit ? ' · cached' : ' · live'}${data.currency === 'EUR' && data.fx_eur_per_usd ? ` · 1 USD = €${data.fx_eur_per_usd.toFixed(4)}` : ''}`}
          large
        />
      </div>

      {!(histError && !hist) && (
        <section style={{ marginBottom: 40 }}>
          <h2 className="label-s" style={{ marginBottom: 12 }}>NET WORTH · DAILY</h2>
          {!histNow ? (
            // Section stays mounted while the series loads or the currency
            // flips, so the accounts table below never jumps — a hairline
            // skeleton slot, per the house loading treatment.
            <div style={{ height: 160, borderTop: 'var(--hairline)', borderBottom: 'var(--hairline)' }} />
          ) : histNow.series.length >= 2 && trendFirst && trendLast ? (
            <>
              <NavSparkline series={histNow.series} currency={histNow.currency} compactAxis />
              <div className="label-s" style={{ marginTop: 8, color: 'var(--ink-3)' }}>
                {fmtDay(trendFirst.x)} — {fmtDay(trendLast.x)} · recorded nightly, UTC
              </div>
            </>
          ) : (
            <p style={{ margin: '12px 0 0', fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)' }}>
              {histNow.rows.length === 0
                ? 'Daily collection has not started — the trend appears once nightly records exist.'
                : trendFirst
                  ? `Trend begins ${fmtDay(trendFirst.x)}.`
                  : 'Every recorded day is currently excluded — see data notes below.'}
            </p>
          )}
        </section>
      )}

      {data.accounts.length > 0 && (
        <section style={{ marginBottom: 40 }}>
          <h2 className="label-s" style={{ marginBottom: 12 }}>ACCOUNTS</h2>
          <table className="tbl">
            <tbody>
              {data.accounts.map(a => (
                <tr key={a.label}>
                  <td>
                    {a.label}
                    {a.manual && (
                      <span className="label-s" style={{ color: 'var(--ink-3)', marginLeft: 8, fontSize: 11 }}>
                        MANUAL · TREASURY ESTIMATE — DOES NOT FEED ANY FUND NAV
                      </span>
                    )}
                  </td>
                  <td data-align="right">
                    {a.manual ? (
                      <ManualValueEditor account={a} currency={data.currency} fxEurPerUsd={data.fx_eur_per_usd} onSaved={refresh} />
                    ) : (
                      <span className="num">{a.total_usd == null ? '—' : fmtBase(a.total_usd, data.currency)}</span>
                    )}
                  </td>
                  <td data-align="right">
                    <span className="num">{a.pct_of_total == null ? '—' : `${a.pct_of_total.toFixed(1)}% of total`}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <h2 className="label-s" style={{ marginBottom: 12 }}>BALANCES · BY TOKEN</h2>
      <SortTable
        columns={columns}
        rows={data.rows}
        defaultSort="usd_value"
        defaultDir="desc"
        emptyMessage="No priced balances found."
      />

      {(data.fund_manual_holdings || []).length > 0 && (
        <section style={{ marginTop: 40 }}>
          <h2 className="label-s" style={{ marginBottom: 4 }}>FUND MANUAL HOLDINGS · NOT IN THE TREASURY TOTAL · USD</h2>
          <p style={{ margin: '0 0 12px', fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)' }}>
            Fund-level NAV inputs, managed per fund — a different book from the
            treasury estimates above, and never counted in the total.
          </p>
          <table className="tbl">
            <tbody>
              {(data.fund_manual_holdings || []).map(f => (
                <tr key={f.fund_id}>
                  <td>
                    <Link to={`/fund/${f.fund_id}/holdings`} style={{ color: 'var(--ink)' }}>{f.fund_name}</Link>
                    <span className="label-s" style={{ color: 'var(--ink-3)', marginLeft: 8, fontSize: 11 }}>
                      {f.n_rows} HOLDING{f.n_rows === 1 ? '' : 'S'} · VIEW HOLDINGS →
                    </span>
                  </td>
                  <td data-align="right">
                    <span className="num" style={{ color: 'var(--ink-3)' }}>
                      {f.subtotal_usd !== null ? fmtBase(Number(f.subtotal_usd), 'USD') : '—'}
                    </span>
                    {f.n_unpriced > 0 && (
                      <span className="label-s" style={{ display: 'block', color: 'var(--ink-3)' }}>
                        {f.n_unpriced} UNPRICED EXCLUDED
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* item 29 — a failed source is not a note. Same split as
          WalletActivity.tsx; `historyNotes` are informational by construction. */}
      {errorSkips.length > 0 && (
        <div className="label-s" style={{ marginTop: 32, color: 'var(--oxblood)' }}>
          <h2 className="label-s" style={{ marginBottom: 8 }}>SOURCES UNAVAILABLE — TREASURY FIGURES ARE INCOMPLETE:</h2>
          <ul style={{ margin: 0, paddingLeft: 20, fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 14, letterSpacing: 'normal', textTransform: 'none' }}>
            {errorSkips.map((s, i) => (
              <li key={i}>{s.wallet_label ? `${s.wallet_label} — ${s.reason}` : s.reason}</li>
            ))}
          </ul>
        </div>
      )}
      {(noteSkips.length > 0 || historyNotes.length > 0) && (
        <div className="label-s" style={{ marginTop: 32, color: 'var(--ink-3)' }}>
          <h2 className="label-s" style={{ marginBottom: 8 }}>DATA NOTES:</h2>
          <ul style={{ margin: 0, paddingLeft: 20, fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 14, letterSpacing: 'normal', textTransform: 'none' }}>
            {noteSkips.map((s, i) => (
              <li key={i}>{s.wallet_label ? `${s.wallet_label} — ${s.reason}` : s.reason}</li>
            ))}
            {historyNotes.map((n, i) => (
              <li key={`h${i}`}>{n}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Operator-review ask (2026-07): "Positions/Cash outside of TRES" have no
// API data source at all — this lets a manager type in their own current
// figure instead of a permanent "N/A", with who/when attribution so it's
// clear this is a manual estimate, not a live feed. Stored server-side in
// USD always (single source of truth), but entered/displayed in whichever
// currency the page is currently showing — `account.total_usd` here is
// already display-converted by the backend, and on save we convert the
// typed figure back to USD using the same live rate so nothing drifts.
function ManualValueEditor({ account, currency, fxEurPerUsd, onSaved }: {
  account: AccountEntry; currency: 'USD' | 'EUR'; fxEurPerUsd?: number; onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
        <span className="num">{account.total_usd == null ? 'N/A' : fmtBase(account.total_usd, currency)}</span>
        <button type="button" className="btn-link" style={{ fontSize: 11 }}
                onClick={() => { setValue(account.total_usd != null ? String(account.total_usd) : ''); setEditing(true); setError(null); }}>
          {account.total_usd == null ? 'Set value' : 'Edit'}
        </button>
        <ManualValueHistory label={account.label} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input className="field-input" type="number" value={value}
               placeholder={`Amount ${currency}`} style={{ width: 140, textAlign: 'right' }}
               onChange={e => setValue(e.target.value)} />
        <button type="button" className="btn-link" disabled={busy || !value} onClick={async () => {
          setBusy(true); setError(null);
          try {
            // Convert the typed display-currency figure back to USD before
            // sending — the server always stores/accepts amount_usd.
            const typed = Number(value);
            const amountUsd = currency === 'EUR' && fxEurPerUsd
              ? typed / fxEurPerUsd
              : typed;
            await api(`/ops/wallet-balances/manual/${encodeURIComponent(account.label)}`, {
              method: 'PUT', body: { amount_usd: amountUsd },
            });
            setEditing(false);
            onSaved();
          } catch (e: any) {
            setError(e?.payload?.detail || e?.message || 'Failed to save.');
          } finally { setBusy(false); }
        }}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn-link" style={{ color: 'var(--ink-3)' }}
                onClick={() => { setEditing(false); setError(null); }}>Cancel</button>
      </div>
      <span className="label-s" style={{ fontSize: 10, color: 'var(--ink-3)' }}>Entered in {currency}.</span>
      {error && <span className="label-s" style={{ fontSize: 11, color: 'var(--oxblood)' }}>{error}</span>}
    </div>
  );
}

interface ManualValueHistoryEntry {
  amount_usd: string;
  updated_by: string | null;
  updated_at: string;
}

// Full multi-editor history (append-only AuditLog rows) — lazy-fetched only
// when expanded, so opening the page doesn't cost an extra request per
// manual row for something most visits never need to see.
function ManualValueHistory({ label }: { label: string }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ManualValueHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (rows !== null) return;
    setLoading(true);
    try {
      const r = await api<ManualValueHistoryEntry[]>(`/ops/wallet-balances/manual/${encodeURIComponent(label)}`);
      setRows(r);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
      <button type="button" className="btn-link" style={{ fontSize: 10 }} onClick={toggle}>
        {open ? 'hide details' : '(all edited details)'}
      </button>
      {open && (
        loading ? (
          <span className="label-s" style={{ fontSize: 10, color: 'var(--ink-3)' }}>Loading…</span>
        ) : !rows || rows.length === 0 ? (
          <span className="label-s" style={{ fontSize: 10, color: 'var(--ink-3)' }}>No edit history yet.</span>
        ) : (
          <table className="tbl" style={{ marginTop: 2 }}>
            <thead>
              <tr>
                <th style={{ fontSize: 10 }}>EDITED BY</th>
                <th style={{ fontSize: 10 }}>WHEN (UTC)</th>
                <th data-align="right" style={{ fontSize: 10 }}>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ fontSize: 11 }}>{r.updated_by || '—'}</td>
                  <td style={{ fontSize: 11 }}>{new Date(r.updated_at).toISOString().slice(0, 16).replace('T', ' ')}</td>
                  <td data-align="right" className="num" style={{ fontSize: 11 }}>${Number(r.amount_usd).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}
